
import React, { useState } from 'react';
import { ExportConfiguration } from '@/lib/exportEngine';
import { ImportEntityType } from '@/types/import';
import { ExportEngine } from '@/lib/exportEngine';
import { Modal } from '@/components/ui/Modal';
import { Button } from '@/components/ui/Button';
import { Card } from '@/components/ui/Card';

interface ExportDialogProps {
  isOpen: boolean;
  onClose: () => void;
  entityType: ImportEntityType;
  data: any[];
  availableFields: string[];
}

const ExportDialog: React.FC<ExportDialogProps> = ({
  isOpen,
  onClose,
  entityType,
  data,
  availableFields,
}) => {
  const [config, setConfig] = useState<Partial<ExportConfiguration>>({
    entity: entityType as any,
    format: 'pdf',
    fields: availableFields.map((f, i) => ({ name: f, label: f, type: 'string', visible: true, order: i })),
    includeHeaders: true,
  });
  const [selectedFields, setSelectedFields] = useState<Set<string>>(new Set(availableFields));

  const toggleField = (field: string) => {
    const newSelected = new Set(selectedFields);
    if (newSelected.has(field)) {
      newSelected.delete(field);
    } else {
      newSelected.add(field);
    }
    setSelectedFields(newSelected);
    const updatedFields = Array.from(newSelected).map((f, i) => ({ name: f, label: f, type: 'string', visible: true, order: i }));
    setConfig({ ...config, fields: updatedFields });
  };

  const selectAll = () => {
    setSelectedFields(new Set(availableFields));
    setConfig({ ...config, fields: availableFields.map((f, i) => ({ name: f, label: f, type: 'string', visible: true, order: i })) });
  };

  const deselectAll = () => {
    setSelectedFields(new Set());
    setConfig({ ...config, fields: [] });
  };

  const handleExport = async () => {
    const engine = new ExportEngine(config);
    const result = await engine.export(data);
    
    if (result.success && result.fileUrl) {
      const extension = config.format === 'excel' ? 'xlsx' : config.format;
      const filename = `${entityType}_export_${new Date().toISOString().split('T')[0]}.${extension}`;
      
      const response = await fetch(result.fileUrl);
      const blob = await response.blob();
      
      const link = document.createElement('a');
      link.href = URL.createObjectURL(blob);
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    }
    onClose();
  };

  return (
    <Modal isOpen={isOpen} onClose={onClose} title={`Export ${entityType}`} size="lg">
      <div className="p-6 space-y-6 bg-slate-50 dark:bg-slate-900 text-slate-900 dark:text-slate-100">
        <div>
          <label className="block text-sm font-medium mb-2 text-slate-700 dark:text-slate-300">Export Format</label>
          <div className="grid grid-cols-4 gap-2">
            {['pdf', 'excel', 'csv', 'html'].map((format: any) => (
              <button
                key={format}
                onClick={() => setConfig({ ...config, format: format as any })}
                className={`px-4 py-2 rounded-lg border-2 transition-colors ${
                  config.format === format
                    ? 'border-blue-600 bg-blue-600/10 text-blue-600 dark:text-blue-400'
                    : 'border-slate-200 dark:border-slate-700 hover:border-slate-300 dark:hover:border-slate-600 text-slate-600 dark:text-slate-400'
                }`}
              >
                {format.toUpperCase()}
              </button>
            ))}
          </div>
        </div>

        <div>
          <div className="flex justify-between items-center mb-2">
            <label className="block text-sm font-medium text-slate-700 dark:text-slate-300">Fields to Export</label>
            <div className="flex gap-2">
              <Button 
                size="sm" 
                variant="secondary" 
                className="text-xs bg-slate-200 hover:bg-slate-300 text-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-100 border-none" 
                onClick={selectAll}
              >
                Select All
              </Button>
              <Button 
                size="sm" 
                variant="secondary" 
                className="text-xs bg-slate-200 hover:bg-slate-300 text-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-100 border-none" 
                onClick={deselectAll}
              >
                Deselect All
              </Button>
            </div>
          </div>
          <Card className="p-4 bg-white dark:bg-slate-800 border-slate-200 dark:border-slate-700">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
              {availableFields.map(field => (
                <label key={field} className="flex items-center gap-2 cursor-pointer group">
                  <input
                    type="checkbox"
                    checked={selectedFields.has(field)}
                    onChange={() => toggleField(field)}
                    className="rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
                  />
                  <span className="text-sm text-slate-600 dark:text-slate-400 group-hover:text-slate-900 dark:group-hover:text-slate-200">{field}</span>
                </label>
              ))}
            </div>
          </Card>
        </div>

        {config.format === 'csv' && (
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="include-headers"
              checked={config.includeHeaders}
              onChange={(e) => setConfig({ ...config, includeHeaders: e.target.checked })}
              className="rounded border-slate-300 dark:border-slate-600 text-blue-600 focus:ring-blue-500"
            />
            <label htmlFor="include-headers" className="text-sm text-slate-600 dark:text-slate-400">Include headers</label>
          </div>
        )}

        <div className="bg-slate-100 dark:bg-slate-800/50 p-4 rounded-lg border border-slate-200 dark:border-slate-700">
          <div className="text-sm text-slate-600 dark:text-slate-400">
            <strong className="text-slate-900 dark:text-slate-200">{data.length}</strong> records will be exported with{' '}
            <strong className="text-slate-900 dark:text-slate-200">{(config.fields as any)?.length || 0}</strong> fields
          </div>
        </div>

        <div className="flex justify-end gap-3 pt-4 border-t border-slate-200 dark:border-slate-700">
          <Button 
            variant="secondary" 
            className="bg-slate-200 hover:bg-slate-300 text-slate-800 dark:bg-slate-700 dark:hover:bg-slate-600 dark:text-slate-100 border-none" 
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button 
            onClick={handleExport} 
            disabled={!config.fields || (config.fields as any).length === 0} 
            className="bg-blue-600 hover:bg-blue-700 text-white border-none shadow-lg"
          >
            Export
          </Button>
        </div>
      </div>
    </Modal>
  );
};

export default ExportDialog;
